import { useEffect, useMemo, useState } from "react";
import { useParams, Navigate, useSearchParams } from "react-router-dom";
import UI from "../components/eicma/ui_i18n.json";

const API = process.env.REACT_APP_BACKEND_URL || "";
const CATEGORIES = ["moto", "oem", "parts"];
const LANGS = [{ code: "en", label: "English" }, { code: "it", label: "Italiano" }, { code: "zh", label: "简体中文" }, { code: "hi", label: "हिन्दी" }, { code: "vi", label: "Tiếng Việt" }];
const CONTACT = ["name", "company", "job", "hall", "booth", "whatsapp", "phone", "website", "country", "comments"];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i;
const TITLES = { moto: "Motorcycles & scooters", oem: "OEM / ODM for MOHA MOTORS", parts: "Parts, helmets & accessories" };
const box = "w-full rounded-xl bg-[#202228] border border-[#3a3d44] text-white px-4 py-3 text-base focus:outline-none focus:border-[#ff7b21] focus:ring-2 focus:ring-[#ff7b21]/30";
const btn = "rounded-xl px-5 py-3 font-semibold transition-[transform,background-color] active:scale-[0.98]";
const primary = `${btn} bg-[#ff7b21] text-[#151515] font-extrabold disabled:opacity-50`;
const secondary = `${btn} bg-[#2a2c32] text-white border border-[#3a3d44]`;

const asList = v => (Array.isArray(v) ? v : v ? [v] : []);
const matches = (answers, [id, vals]) => asList(answers[id]?.v).some(x => vals.includes(x));
const isVisible = (n, answers) => !n.show_if || (n.show_if.all ? n.show_if.all.every(c => matches(answers, c)) : n.show_if.any.some(c => matches(answers, c)));

function Header() {
  return (
    <header className="border-b-[3px] border-[#ff7b21] pb-4">
      <p className="font-black text-2xl tracking-wide">RĂDĂCINI <span className="text-[#ff7b21]">× MOHA MOTORS</span></p>
      <p className="text-sm text-[#b8bac0]">Romania · International business partnerships · EICMA</p>
      <a href="https://mohamotors.com" target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-sm font-semibold text-[#ff974b] underline" data-testid="eicma-site-link">mohamotors.com ↗</a>
    </header>
  );
}

function Intro({ t, category, onEmailNow, onAnswersFirst }) {
  return (
    <section className="mt-6 rounded-2xl border border-[#35383f] p-5" data-testid="eicma-intro">
      <h1 className="text-2xl font-bold">{t.headline}</h1>
      <p className="mt-1 text-[#ff974b] font-bold" data-testid="eicma-category-title">{TITLES[category]}</p>
      {[t.intro1, t.intro2, t.intro3].map(p => <p key={p.slice(0, 24)} className="mt-3 text-[#d6d7db] leading-relaxed">{p}</p>)}
      <p className="mt-3 text-sm">{t.links}: <a href="https://www.radacini.ro" target="_blank" rel="noopener noreferrer" className="text-[#ff7b21] underline">radacini.ro</a> · <a href="https://mohamotors.com" target="_blank" rel="noopener noreferrer" className="text-[#ff7b21] underline">mohamotors.com</a></p>
      <div className="mt-6 grid sm:grid-cols-2 gap-3">
        <button className={primary} onClick={onEmailNow} data-testid="eicma-email-now">{t.emailNow}</button>
        <button className={secondary} onClick={onAnswersFirst} data-testid="eicma-answers-first">{t.answersFirst}</button>
      </div>
      <p className="mt-3 text-xs text-[#9a9ca3]">{t.emailWhy}</p>
    </section>
  );
}

function EmailBlock({ t, email, setEmail, onNext, nextLabel, children }) {
  const [err, setErr] = useState("");
  const go = () => (EMAIL_RE.test(email.trim()) ? (setErr(""), onNext()) : setErr(t.invalidEmail));
  return (
    <section className="mt-6 rounded-2xl border border-[#35383f] p-5" data-testid="eicma-email-step">
      <label className="block font-semibold mb-1.5">{t.lemail}</label>
      <input type="email" autoComplete="email" className={box} value={email} onChange={e => setEmail(e.target.value)} data-testid="eicma-email" />
      <p className="mt-2 text-xs text-[#9a9ca3]">{t.emailWhy}</p>
      {err && <p className="mt-2 text-sm text-red-400" data-testid="eicma-email-error">{err}</p>}
      {children}
      <button className={`${primary} mt-4`} onClick={go} data-testid="eicma-email-continue">{nextLabel}</button>
    </section>
  );
}

function Identity({ t, contact, setContact, onDone }) {
  return (
    <section className="mt-6 rounded-2xl border border-[#35383f] p-5" data-testid="eicma-identity">
      <h2 className="text-lg font-bold text-[#ff974b]">{t.identityTitle}</h2>
      <p className="text-xs text-[#9a9ca3] mt-1">{t.identityHint}</p>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        {CONTACT.filter(k => k !== "comments").map(k => (
          <label key={k} className="block"><span className="block text-sm mb-1">{t[`l${k === "name" ? "name" : k}`]}</span>
            <input className={box} value={contact[k] || ""} onChange={e => setContact(c => ({ ...c, [k]: e.target.value }))} data-testid={`eicma-contact-${k}`} /></label>
        ))}
      </div>
      <label className="block mt-3"><span className="block text-sm mb-1">{t.lcomments}</span>
        <textarea rows={3} className={box} value={contact.comments || ""} onChange={e => setContact(c => ({ ...c, comments: e.target.value }))} data-testid="eicma-contact-comments" /></label>
      <div className="mt-4 flex flex-wrap gap-3">
        <button className={primary} onClick={onDone} data-testid="eicma-identity-continue">{t.continue}</button>
        <button className={secondary} onClick={onDone} data-testid="eicma-identity-skip">{t.skip}</button>
      </div>
    </section>
  );
}

function QuestionCard({ t, node, tr, answer, setAnswer, onNext, onBack, canBack, index, total }) {
  const a = answer || {};
  const sel = asList(a.v);
  const single = node.type === "single";
  const toggle = v => setAnswer({ ...a, v: single ? v : sel.includes(v) ? sel.filter(x => x !== v) : [...sel, v] });
  const pick = v => (single && v !== "other" ? (setAnswer({ ...a, v }), setTimeout(() => onNext({ ...a, v }), 150)) : toggle(v));
  const options = [...Object.keys(node.options || {}).filter(k => k !== "other"), "other", "dk", "pnts"];
  const label = k => (k === "other" ? t.other : k === "dk" ? t.dk : k === "pnts" ? t.pnts : tr(`${node.id}.o.${k}`));
  return (
    <section className="mt-6 rounded-2xl border border-[#35383f] p-5" data-testid={`eicma-node-${node.id}`}>
      <p className="text-xs text-[#9a9ca3]" data-testid="eicma-progress">{t.question} {index + 1} {t.of} {total}</p>
      <h2 className="mt-1 text-lg font-bold">{tr(`${node.id}.q`)}</h2>
      {node.help && <p className="mt-1 text-xs text-[#9a9ca3]">{tr(`${node.id}.help`)}</p>}
      {node.type === "text" ? (
        <textarea rows={3} className={`${box} mt-4`} placeholder={t.answerPlaceholder} value={a.text || ""} onChange={e => setAnswer({ ...a, text: e.target.value })} data-testid="eicma-text-answer" />
      ) : (
        <div className="mt-4 grid gap-2">
          {options.map(k => (
            <button key={k} onClick={() => pick(k)} data-testid={`eicma-opt-${k}`}
              className={`text-left rounded-xl border px-4 py-3 transition-colors ${sel.includes(k) ? "border-[#ff7b21] bg-[#ff7b21]/15" : "border-[#3a3d44] bg-[#1a1c21] hover:border-[#ff7b21]/60"} ${["dk", "pnts"].includes(k) ? "text-sm text-[#b8bac0]" : ""}`}>
              {label(k)}
            </button>
          ))}
          {sel.includes("other") && <input className={box} placeholder={t.other} value={a.other || ""} onChange={e => setAnswer({ ...a, other: e.target.value })} data-testid="eicma-other-text" />}
          <input className={`${box} text-sm`} placeholder={t.notePlaceholder} value={a.note || ""} onChange={e => setAnswer({ ...a, note: e.target.value })} data-testid="eicma-note" />
        </div>
      )}
      <div className="mt-5 flex flex-wrap gap-3">
        {canBack && <button className={secondary} onClick={onBack} data-testid="eicma-back">{t.back}</button>}
        <button className={primary} onClick={onNext} data-testid="eicma-next">{t.next}</button>
        <button className="text-sm text-[#9a9ca3] underline px-2" onClick={() => { const s = { v: single ? "skipped" : ["skipped"] }; setAnswer(s); onNext(s); }} data-testid="eicma-skip-question">{t.skipQuestion}</button>
      </div>
    </section>
  );
}

function ResumeBox({ t, link }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-4 rounded-xl bg-[#1a1c21] border border-[#ff7b21]/40 p-4" data-testid="eicma-resume-box">
      <p className="font-bold">{t.resumeTitle}</p>
      <p className="text-xs text-[#9a9ca3] mt-1">{t.resumeExplain}</p>
      <input readOnly value={link} className={`${box} mt-2 text-xs`} data-testid="eicma-resume-link" onFocus={e => e.target.select()} />
      <button className={`${secondary} mt-2 text-sm`} onClick={() => { navigator.clipboard.writeText(link); setCopied(true); }} data-testid="eicma-copy-link">{copied ? t.copied : t.copyLink}</button>
    </div>
  );
}

function Summary({ t, rows, contact, email }) {
  return (
    <div className="mt-4 text-sm space-y-2" data-testid="eicma-summary">
      <p className="font-bold">{t.yourAnswers}</p>
      <p><span className="text-[#9a9ca3]">{t.lemail}:</span> {email}</p>
      {CONTACT.filter(k => contact[k]).map(k => <p key={k}><span className="text-[#9a9ca3]">{t[`l${k}`]}:</span> {contact[k]}</p>)}
      {rows.map(([q, a]) => <p key={q}><span className="text-[#9a9ca3]">{q}</span><br />{a}</p>)}
    </div>
  );
}

export default function EicmaPage() {
  const { category } = useParams();
  const [params, setParams] = useSearchParams();
  const [flow, setFlow] = useState(null);
  const [lang, setLang] = useState("");
  const [phase, setPhase] = useState("lang");
  const [emailFirst, setEmailFirst] = useState(false);
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState({});
  const [answers, setAnswers] = useState({});
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [consent, setConsent] = useState(false);
  const [token, setToken] = useState("");
  const [notice, setNotice] = useState("");
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [returnTo, setReturnTo] = useState("questions");
  const t = UI[lang] || UI.en;
  const tr = k => flow?.i18n?.[k]?.[lang] || flow?.i18n?.[k]?.en || k;

  useEffect(() => {
    if (!CATEGORIES.includes(category)) return;
    fetch(`${API}/api/public/eicma/flow/${category}`).then(r => r.json()).then(setFlow).catch(() => setFlow({ nodes: [], i18n: {} }));
  }, [category]);

  useEffect(() => {
    const resume = params.get("resume");
    if (!resume) return;
    fetch(`${API}/api/public/eicma/drafts/${encodeURIComponent(resume)}`).then(r => (r.ok ? r.json() : Promise.reject()))
      .then(({ state: s }) => {
        setLang(s.lang || "en"); setPhase(s.phase || "questions"); setEmail(s.email || ""); setContact(s.contact || {});
        setAnswers(s.answers || {}); setCurrent(s.current || null); setHistory(s.history || []); setEmailFirst(!!s.emailFirst);
        setToken(resume); setNotice("restored");
      })
      .catch(() => setNotice("expired"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (lang) document.documentElement.lang = lang; }, [lang]);
  const visible = useMemo(() => (flow?.nodes || []).filter(n => isVisible(n, answers)), [flow, answers]);
  if (!CATEGORIES.includes(category)) return <Navigate to="/" replace />;

  const node = visible.find(n => n.id === current) || visible[0];
  const idx = node ? visible.indexOf(node) : 0;
  const startQuestions = () => { setNotice(""); setCurrent(visible[0]?.id || null); setPhase("questions"); };
  const next = (nodeAnswer) => {
    const merged = nodeAnswer && typeof nodeAnswer === "object" && !nodeAnswer.nativeEvent ? { ...answers, [node.id]: nodeAnswer } : answers;
    const vis = (flow?.nodes || []).filter(n => isVisible(n, merged));
    const nextNode = vis[vis.findIndex(n => n.id === node.id) + 1];
    setHistory(h => [...h, node.id]);
    if (nextNode) setCurrent(nextNode.id); else setPhase("final");
  };
  const back = () => { const prev = history[history.length - 1]; setHistory(h => h.slice(0, -1)); setCurrent(prev); };
  const path = visible.map(n => n.id).filter(id => answers[id]);
  const rows = visible.filter(n => answers[n.id]).map(n => {
    const a = answers[n.id];
    const labels = asList(a.v).map(v => (v === "dk" ? t.dk : v === "pnts" ? t.pnts : v === "other" ? t.other : v === "skipped" ? "—" : tr(`${n.id}.o.${v}`)));
    return [tr(`${n.id}.q`), [labels.join(", "), a.other, a.text, a.note].filter(Boolean).join(" — ")];
  });

  const saveLater = async () => {
    const state = { lang, phase, email, contact, answers, current: node?.id, history, emailFirst };
    const r = token
      ? await fetch(`${API}/api/public/eicma/drafts/${encodeURIComponent(token)}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ state }) })
      : await fetch(`${API}/api/public/eicma/drafts`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ state }) });
    if (!r.ok) { setNotice("error"); return; }
    if (!token) { const j = await r.json(); setToken(j.token); setParams({ resume: j.token }, { replace: true }); }
    setNotice("saved");
  };

  const submit = async () => {
    if (!EMAIL_RE.test(email.trim()) || !consent) { setNotice("required"); return; }
    setSending(true);
    const r = await fetch(`${API}/api/public/eicma/v2/submit`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ category, language: lang, email: email.trim(), consent, contact, answers: Object.fromEntries(visible.filter(n => answers[n.id]).map(n => [n.id, answers[n.id]])), path, draft_token: token }),
    }).catch(() => null);
    setSending(false);
    if (!r || !r.ok) { setNotice(r?.status === 429 ? "rate" : "error"); return; }
    setNotice("");
    setResult(await r.json());
    setParams({}, { replace: true });
    setPhase("done");
  };

  const download = () => {
    const text = [`${TITLES[category]} — ${result.id}`, `${t.lemail}: ${email}`, ...CONTACT.filter(k => contact[k]).map(k => `${t[`l${k}`]}: ${contact[k]}`), "", ...rows.map(([q, a]) => `${q}\n${a}\n`)].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
    a.download = `EICMA_${result.id}.txt`;
    a.click();
  };

  const noticeText = { restored: t.draftRestored, expired: t.resumeExpired, required: t.required, error: t.error, rate: t.rate }[notice];
  const resumeLink = token ? `${window.location.origin}/eicma/${category}?resume=${token}` : "";

  return (
    <div className="min-h-screen bg-[#101114] text-[#f4f4f4] font-['Exo_2']" data-testid="eicma-page">
      <main className="max-w-2xl mx-auto px-5 py-6">
        <Header />
        {phase !== "lang" && phase !== "done" && (
          <div className="mt-4 flex flex-wrap gap-2 print:hidden" data-testid="eicma-lang-switch">
            {LANGS.map(l => <button key={l.code} onClick={() => setLang(l.code)} data-testid={`eicma-switch-${l.code}`} className={`px-3 py-1.5 rounded-lg text-sm border ${lang === l.code ? "bg-[#ff7b21] text-black border-[#ff7b21] font-bold" : "border-[#3a3d44] text-[#b8bac0]"}`}>{l.label}</button>)}
          </div>
        )}
        {noticeText && <p className="mt-4 rounded-lg bg-[#282a30] p-3 text-sm" aria-live="polite" data-testid="eicma-status">{noticeText}</p>}
        {phase === "lang" && (
          <section className="mt-8" data-testid="eicma-language-step">
            <h1 className="text-xl font-bold mb-1">Choose your language</h1>
            <p className="text-sm text-[#b8bac0] mb-5">Scegli la lingua · 选择语言 · भाषा चुनें · Chọn ngôn ngữ</p>
            <div className="grid gap-3">{LANGS.map(l => <button key={l.code} data-testid={`eicma-lang-${l.code}`} onClick={() => { setLang(l.code); setPhase("intro"); }} className="text-left rounded-xl border border-[#3a3d44] bg-[#1a1c21] px-5 py-4 text-lg font-semibold hover:border-[#ff7b21]">{l.label}</button>)}</div>
          </section>
        )}
        {phase === "intro" && <Intro t={t} category={category} onEmailNow={() => { setEmailFirst(true); setPhase("email"); }} onAnswersFirst={startQuestions} />}
        {phase === "email" && <EmailBlock t={t} email={email} setEmail={setEmail} nextLabel={t.continue} onNext={() => setPhase("identity")} />}
        {phase === "identity" && <Identity t={t} contact={contact} setContact={setContact} onDone={() => (returnTo === "final" ? setPhase("final") : history.length || current ? setPhase("questions") : startQuestions())} />}
        {phase === "questions" && !node && <p className="mt-6 text-sm text-[#9a9ca3]" data-testid="eicma-loading">{flow ? t.error : "…"}</p>}
        {phase === "questions" && node && (
          <QuestionCard key={node.id} t={t} node={node} tr={tr} answer={answers[node.id]} setAnswer={a => setAnswers(s => ({ ...s, [node.id]: a }))}
            onNext={next} onBack={back} canBack={history.length > 0} index={idx} total={visible.length} />
        )}
        {phase === "final" && (
          <EmailBlock t={t} email={email} setEmail={setEmail} nextLabel={sending ? t.sending : t.send} onNext={submit}>
            <h2 className="mt-4 text-lg font-bold text-[#ff974b]">{t.finalTitle}</h2>
            <p className="text-xs text-[#9a9ca3]">{t.finalHint}</p>
            <button className={`${secondary} mt-3 text-sm`} onClick={() => { setReturnTo("final"); setPhase("identity"); }} data-testid="eicma-edit-contact">{t.editContact}</button>
            <h3 className="mt-5 font-bold">{t.privacyTitle}</h3>
            <p className="mt-1 text-xs text-[#c3c3c3] leading-relaxed" data-testid="eicma-privacy">{t.privacy}</p>
            <label className="mt-3 flex gap-3 items-start"><input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-1 w-5 h-5 accent-[#ff7b21]" data-testid="eicma-consent" /><span className="text-sm">{t.consentLabel}</span></label>
            <button className={`${secondary} mt-3 text-sm`} onClick={() => { setCurrent(history[history.length - 1]); setHistory(h => h.slice(0, -1)); setPhase("questions"); }} data-testid="eicma-final-back">{t.back}</button>
          </EmailBlock>
        )}
        {["questions", "identity", "final"].includes(phase) && (
          <div className="mt-4 print:hidden">
            {phase === "questions" && <button className="text-sm text-[#ff974b] underline mr-4" onClick={() => { setReturnTo("questions"); setPhase("identity"); }} data-testid="eicma-open-contact">{t.editContact}</button>}
            <button className="text-sm text-[#ff974b] underline" onClick={saveLater} data-testid="eicma-save-later">{t.saveLater}</button>
            {notice === "saved" && <ResumeBox t={t} link={resumeLink} />}
          </div>
        )}
        {phase === "done" && result && (
          <section className="mt-6 rounded-2xl border border-[#ff7b21]/50 p-5" data-testid="eicma-success">
            <p className="text-lg font-bold">{t.success} <span className="text-[#ff7b21]" data-testid="eicma-reference">{result.id}</span></p>
            <p className="mt-2 text-[#d6d7db]">{t.successNote}</p>
            <div className="mt-4 flex flex-wrap gap-3 print:hidden">
              <button className={secondary} onClick={() => window.print()} data-testid="eicma-print">{t.print}</button>
              <button className={secondary} onClick={download} data-testid="eicma-download">{t.download}</button>
            </div>
            <Summary t={t} rows={rows} contact={contact} email={email} />
          </section>
        )}
      </main>
    </div>
  );
}
